import { motion, AnimatePresence } from 'framer-motion';
import React from 'react';

export function PageContainer(props: { children: React.ReactNode }) {
    return <div className="projectWorkspace">{props.children}</div>;
}

export function SectionCard(props: { id?: string; children: React.ReactNode }) {
    return (
        <section id={props.id} className="projectSectionCard">
            {props.children}
        </section>
    );
}

export function SectionHeader(props: {
    icon?: React.ReactNode;
    title: string;
    description?: string;
    badge?: React.ReactNode;
    actions?: React.ReactNode;
}) {
    return (
        <div className="projectSectionHeader">
            <div className="projectSectionHeaderMain">
                {props.icon ? <span className="projectSectionIcon">{props.icon}</span> : null}
                <div className="projectSectionHeaderCopy">
                    <div className="projectSectionHeaderTitleRow">
                        <h2 className="projectSectionTitle">{props.title}</h2>
                        {props.badge ? <div className="projectSectionBadgeSlot">{props.badge}</div> : null}
                    </div>
                    {props.description ? <p className="projectSectionDescription">{props.description}</p> : null}
                </div>
            </div>

            {props.actions ? <div className="projectSectionActions">{props.actions}</div> : null}
        </div>
    );
}

export function InfoGrid(props: { children: React.ReactNode }) {
    return <div className="projectInfoGrid">{props.children}</div>;
}

export function AccordionItem(props: {
    title: string;
    summary?: string;
    badge?: React.ReactNode;
    actions?: React.ReactNode;
    children: React.ReactNode;
    defaultOpen?: boolean;
    compact?: boolean;
}) {
    const [open, setOpen] = React.useState(Boolean(props.defaultOpen));

    return (
        <div className={`projectAccordion${props.compact ? ' isCompact' : ''}`}>
            <div className="projectAccordionHeader">

                <div
                    className="projectAccordionTrigger"
                    role="button"
                    tabIndex={0}
                    aria-expanded={open}
                    onClick={() => setOpen((value) => !value)}
                    onKeyDown={(e) => {

                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpen((value) => !value);
                        }
                    }}
                >
                    <span className={`projectAccordionCaret${open ? ' isOpen' : ''}`} aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                            <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </span>
                    <span className="projectAccordionCopy">
                        <strong className="projectAccordionTitle">{props.title}</strong>
                        {props.summary ? <span className="projectAccordionSummary">{props.summary}</span> : null}
                    </span>
                </div>

                <div className="projectAccordionMeta">
                    {props.badge ? <div>{props.badge}</div> : null}
                    {props.actions ? <div>{props.actions}</div> : null}
                </div>
            </div>

            {/* @ts-expect-error - React 19 typing issue with AnimatePresence */}
            <AnimatePresence initial={false}>
                {open ? (
                    <motion.div
                        key="content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                            open: { opacity: 1, height: 'auto' },
                            collapsed: { opacity: 0, height: 0 },
                        }}
                        transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                    >
                        <div className="projectAccordionBody">{props.children}</div>
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </div>
    );
}

export function TableContainer(props: {
    title: string;
    description?: string;
    children: React.ReactNode;
    actions?: React.ReactNode;
}) {
    return (
        <div className="projectTableContainer">
            <div className="projectTableHeader">
                <div>
                    <h3 className="projectTableTitle">{props.title}</h3>
                    {props.description ? <p className="projectTableDescription">{props.description}</p> : null}
                </div>
                {props.actions ? <div className="projectTableActions">{props.actions}</div> : null}
            </div>
            <div className="projectTableScroller">{props.children}</div>
        </div>
    );
}

export function StatusBadge(props: { label: string; tone?: 'neutral' | 'info' | 'success' | 'warning' }) {
    return <span className={`projectStatusBadge${props.tone ? ` is-${props.tone}` : ''}`}>{props.label}</span>;
}

export function ProjectIcon(props: { name: string }) {
    const normalized = props.name.toLowerCase();

    if (normalized.includes('inform')) return <Glyph
        path="M12 17v-5m0-3h.01M5 21h14a2 2 0 0 0 2-2V7.8a2 2 0 0 0-.58-1.4l-4.82-4.82A2 2 0 0 0 14.2 1H5a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2Z"/>;
    if (normalized.includes('requer')) return <Glyph
        path="M7 4h10a2 2 0 0 1 2 2v12l-3-2-3 2-3-2-3 2V6a2 2 0 0 1 2-2Zm2 4h6m-6 4h6"/>;
    if (normalized.includes('autoriz')) return <Glyph path="m9 12 2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>;
    if (normalized.includes('contrat')) return <Glyph
        path="M8 7V5a4 4 0 1 1 8 0v2m-9 14h10a2 2 0 0 0 2-2V9H5v10a2 2 0 0 0 2 2Z"/>;
    if (normalized.includes('contable')) return <Glyph
        path="M3 6h18M7 3v3m10-3v3M6 10h12m-8 4h4m-7 7h10a2 2 0 0 0 2-2V8H5v11a2 2 0 0 0 2 2Z"/>;
    if (normalized.includes('seguimiento')) return <Glyph path="M4 13h4l2-5 4 8 2-3h4"/>;
    if (normalized.includes('certific')) return <Glyph
        path="M9 12h6m-6 4h6M14 2H6a2 2 0 0 0-2 2v16l4-3h10a2 2 0 0 0 2-2V8l-6-6Z"/>;
    if (normalized.includes('comunic')) return <Glyph
        path="M7 10h10M7 14h6m-8 7h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z"/>;
    if (normalized.includes('reunion')) return <Glyph path="M4 5h16v10H7l-3 3V5Z"/>;
    if (normalized.includes('plazo')) return <Glyph path="M12 7v5l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>;
    if (normalized.includes('hito')) return <Glyph path="m5 12 4 4L19 6"/>;
    return <Glyph path="M5 12h14M12 5v14"/>;
}

function Glyph(props: { path: string }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
             strokeLinejoin="round">
            <path d={props.path}/>
        </svg>
    );
}